import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import {
  type IAdminWarehouseProductList,
  type IProductFeatureFlag,
} from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import { Alert, Space, Typography } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { featureFlagTags } from '../../fragments/feature-flag-tags';
import {
  getTableFeatureFlagsColumnFilters,
  getTableFeatureFlagsColumnOnFilter,
} from '../../fragments/table-feature-flags-column';
import { getColumnSearch } from '../../fragments/table-get-column-search';
import { renderProductCode } from '../../fragments/table-product-code';
import { SPACING } from '../../theme/tokens';
import Link from '../link';
import Table from '../table';

interface DataType extends IAdminWarehouseProductList {
  key: string;
}

const { Text } = Typography;

function WarehouseProductsTable(): React.ReactElement {
  const warehouseProducts = useSelector(
    (s: RootState) => s.Downloader.warehouse.products,
  );
  const downloaderInProcess = useSelector(
    (s: RootState) => s.Downloader.inProcess,
  );
  const featureFlagsState = useSelector((s: RootState) => s.FeatureFlags);
  const warehouseState = useSelector((s: RootState) => s.Warehouse);

  const dataSource = useMemo(
    (): DataType[] =>
      warehouseProducts?.map(
        (product: IAdminWarehouseProductList): DataType => ({
          key: product.uuid,
          ...product,
        }),
      ) ?? [],
    [warehouseProducts],
  );

  const { warehousesName, brandsName } = useMemo(() => {
    const warehousesName: Set<string> = new Set<string>();
    const brandsName: Set<string> = new Set<string>();
    dataSource.forEach((product: DataType): void => {
      warehousesName.add(product.warehouse.name);
      brandsName.add(product.brand);
    });
    return { warehousesName, brandsName };
  }, [dataSource]);

  const columns = useMemo(
    (): ColumnsType<object> => [
      {
        title: 'Warehouse',
        dataIndex: 'warehouse',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.warehouse.name, b.warehouse.name),
        filterSearch: true,
        filters: [...warehousesName]
          .map((warehouse: string) => ({
            text: warehouse,
            value: warehouse,
          }))
          .sort((a, b) => stringCompare(a.text, b.text)),
        onFilter: (value: string, record: DataType): boolean =>
          record.warehouse.name === value,
        render: (warehouse: IAdminWarehouseProductList): string =>
          warehouse.name,
        width: '10%',
      },
      {
        title: 'Brand',
        dataIndex: 'brand',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.brand, b.brand),
        filterSearch: true,
        filters: [...brandsName]
          .map((brand: string) => ({
            text: brand,
            value: brand,
          }))
          .sort((a, b) => stringCompare(a.text, b.text)),
        onFilter: (value: string, record: DataType): boolean =>
          record.brand === value,
        width: '10%',
      },
      {
        title: 'Name',
        dataIndex: 'name',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.name, b.name),
        render: (name: string, record: DataType): React.ReactElement => (
          <Link
            to={`/downloader/warehouse-product/${record.uuid}`}
            openInNewTab
            label={name}
          />
        ),
        width: '40%',
        ...getColumnSearch('name'),
      },
      {
        title: 'Price',
        dataIndex: 'price',
        sorter: {
          compare: (a: DataType, b: DataType): number => a.price - b.price,
          multiple: 100,
        },
        width: '10%',
      },
      {
        title: 'Stock',
        dataIndex: 'count',
        sorter: {
          compare: (a: DataType, b: DataType): number => a.count - b.count,
          multiple: 1,
        },
        filterSearch: true,
        filters: [
          {
            text: 'In stock',
            value: 'inStock',
          },
          {
            text: 'Full stock',
            value: 'fullStock',
          },
          {
            text: 'Low stock',
            value: 'lowStock',
          },
          {
            text: 'Out of stock',
            value: 'outOfStock',
          },
        ],
        onFilter: (value: string, record: DataType): boolean => {
          if (value === 'inStock') {
            return record.count > 0;
          }

          if (value === 'fullStock') {
            return record.count >= 5;
          }

          if (value === 'lowStock') {
            return record.count > 0 && record.count < 5;
          }

          if (value === 'outOfStock') {
            return record.count === 0;
          }

          return false;
        },
        render: (value: number): React.ReactElement => {
          if (value >= 5) {
            return (
              <Text strong type="success">
                {value}
              </Text>
            );
          }
          if (value >= 2) {
            return (
              <Text strong type="warning">
                {value}
              </Text>
            );
          }

          return (
            <Text strong type="danger">
              {value}
            </Text>
          );
        },
        width: '5%',
      },
      {
        title: 'Product code',
        dataIndex: 'productCode',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.productCode, b.productCode),
        filterSearch: true,
        filters: [
          {
            text: 'With warehouse URL',
            value: 'withWarehouseUrl',
          },
          {
            text: 'Without warehouse URL',
            value: 'withoutWarehouseUrl',
          },
        ],
        onFilter: (value: string, record: DataType): boolean => {
          if (value === 'withWarehouseUrl') {
            return record.url !== undefined;
          }

          if (value === 'withoutWarehouseUrl') {
            return record.url === undefined;
          }

          return record.productCode === value;
        },
        render: (productCode: string, record: DataType): React.ReactElement =>
          renderProductCode(productCode, record.url),
        width: '10%',
      },
      {
        title: 'MPN',
        dataIndex: 'mpn',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.mpn, b.mpn),
        filterSearch: true,
        onFilter: (value: string, record: DataType): boolean =>
          record.mpn === value,
        width: '5%',
      },
      {
        title: 'GTIN',
        dataIndex: 'gtin',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.gtin, b.gtin),
        filterSearch: true,
        onFilter: (value: string, record: DataType): boolean =>
          record.gtin === value,
        width: '5%',
      },
      {
        title: 'Feature flags',
        dataIndex: 'featureFlags',
        sorter: (a: DataType, b: DataType): number =>
          a.featureFlags.length - b.featureFlags.length,
        filterSearch: true,
        filters: getTableFeatureFlagsColumnFilters({
          dataSource,
          FeatureFlags: featureFlagsState,
        }),
        onFilter: getTableFeatureFlagsColumnOnFilter,
        render: (
          productFeatureFlags: IProductFeatureFlag[],
        ): React.ReactElement[] =>
          featureFlagTags({
            productFeatureFlags,
            FeatureFlags: featureFlagsState,
            Warehouse: warehouseState,
          }),
        width: '10%',
      },
      {
        title: 'Actions',
        sorter: {
          compare: (a: DataType, b: DataType): number =>
            stringCompare(a.uuid, b.uuid),
          multiple: 2,
        },
        filters: [
          {
            text: 'Paired with product',
            value: 'pairedWithProduct',
          },
          {
            text: 'Not paired with product',
            value: 'notPairedWithProduct',
          },
        ],
        onFilter: (value: string, record: DataType): boolean => {
          if (value === 'pairedWithProduct') {
            return record.product.uuid !== undefined;
          }

          if (value === 'notPairedWithProduct') {
            return record.product.uuid === undefined;
          }

          return false;
        },
        render: (_text: string, record: DataType): React.ReactElement => (
          <Space size={SPACING.lg} align="start">
            {record.product.uuid && (
              <Link to={`/product/${record.product.uuid}`} openInNewTab />
            )}
          </Space>
        ),
        width: '5%',
      },
    ],
    [
      warehousesName,
      brandsName,
      featureFlagsState.featureFlags,
      featureFlagsState.updatedAt,
      dataSource,
      warehouseState.warehouses,
    ],
  );

  if (warehouseProducts === null) {
    return (
      <Alert description="Warehouse products not found" type="error" showIcon />
    );
  }

  return (
    <Space size={SPACING.lg} orientation="vertical" style={{ width: '100%' }}>
      <Table
        columns={columns}
        dataSource={dataSource}
        isLoading={downloaderInProcess}
        emptyText="No warehouse products found"
      />
    </Space>
  );
}

export default WarehouseProductsTable;
